import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import {
  ApiError,
  branchesApi,
  financialYearsApi,
  goodsVouchersApi,
  invoiceSeriesApi,
  itemsApi,
  ledgersApi,
  partyAddressesApi,
  voucherClassesApi,
  voucherTypesApi,
} from '../../lib/api';
import type {
  Branch,
  FinancialYear,
  GoodsAdditionalChargePayload,
  GoodsDirection,
  GoodsVoucher,
  GoodsVoucherLinePayload,
  GoodsVoucherPayload,
  InvoiceSeries,
  Ledger,
  VoucherClass,
  VoucherType,
} from '../../lib/types';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field, Select, TextInput } from '../../components/Field';
import { Modal } from '../../components/Modal';
import { Toggle } from '../../components/Toggle';
import { TypeAhead } from '../../components/TypeAhead';
import { useCompanyStore } from '../../stores/company';
import { useUiStore } from '../../stores/ui';
import { useShortcuts } from '../../lib/keys';
import {
  clearGoodsVoucherDraft,
  loadGoodsVoucherDraft,
  saveGoodsVoucherDraft,
  type LocalGoodsVoucherDraft,
} from './goodsVoucherDraftStore';

type DraftLine = GoodsVoucherLinePayload & {
  key: string;
  itemName?: string;
  unitOptions: Array<{ id: string; unitId: string; label: string }>;
};
type Preview = {
  totals?: {
    counterpartyPayable?: string;
    principalValueAfterDiscounts?: string;
    roundOff?: string;
    taxes?: Record<string, string>;
  };
  documentType?: string;
};
const today = () => new Date().toISOString().slice(0, 10);
const money = (v: string, scale = 4) => !v.trim() || new RegExp(`^\\d+(?:\\.\\d{1,${scale}})?$`).test(v);
const optionalDecimal = (value: string | undefined) => {
  const normalized = value?.trim() ?? '';
  return normalized || undefined;
};
const line = (): DraftLine => ({
  key: crypto.randomUUID(),
  itemId: '',
  itemUnitId: '',
  actualQuantity: '',
  billedQuantity: '',
  freeQuantity: '0',
  unitRate: '',
  discountPercent: '',
  discountAmount: '',
  discountTradeAmount: '',
  itcEligibility: 'eligible',
  narration: '',
  unitOptions: [],
});
const charge = (): GoodsAdditionalChargePayload & { key: string } => ({
  key: crypto.randomUUID(),
  kind: 'freight',
  amount: '',
  ledgerId: '',
  apportionmentBasis: 'value',
  capitaliseIntoStock: true,
  narration: '',
});
function formatTime(value: string) {
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}
function saveDownload(file: { blob: Blob; filename: string }) {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Chrome may consume the download asynchronously after click().
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return file.filename;
}
function preferredGoodsVoucherType(types: VoucherType[], direction: GoodsDirection) {
  return (
    types.find(
      (type) =>
        type.isActive &&
        type.code === direction &&
        type.baseBehaviourCode === direction &&
        type.allowsFinancialEntries &&
        type.allowsInventoryEntries &&
        type.allowsBillReferences
    ) ??
    types.find(
      (type) =>
        type.isActive &&
        type.baseBehaviourCode === direction &&
        type.allowsFinancialEntries &&
        type.allowsInventoryEntries &&
        type.allowsBillReferences
    )
  );
}
function invoiceSeriesMatches(
  series: InvoiceSeries,
  companyId: string,
  branchId: string,
  voucherTypeId: string,
  direction: GoodsDirection,
  types: VoucherType[],
  branches: Branch[]
) {
  return (
    series.companyId === companyId &&
    series.isActive &&
    series.branchId === branchId &&
    branches.some((branch) => branch.id === series.branchId && branch.isActive) &&
    series.voucherTypeId === voucherTypeId &&
    types.some(
      (type) =>
        type.id === series.voucherTypeId &&
        type.isActive &&
        type.baseBehaviourCode === direction &&
        type.allowsFinancialEntries &&
        type.allowsInventoryEntries &&
        type.allowsBillReferences
    )
  );
}
function reconcileGoodsDraftMasters(
  payload: GoodsVoucherPayload,
  direction: GoodsDirection,
  companyId: string,
  types: VoucherType[],
  branches: Branch[],
  years: FinancialYear[],
  classes: VoucherClass[],
  series: InvoiceSeries[]
): GoodsVoucherPayload {
  const next = { ...payload };
  const goodType = preferredGoodsVoucherType(types, direction);
  const branch = branches.find((entry) => entry.isActive && entry.isPrimary) ?? branches.find((entry) => entry.isActive);
  const year = years.find((entry) => !entry.isClosed) ?? years[0];
  const validType = types.some(
    (type) =>
      type.id === next.voucherTypeId &&
      type.isActive &&
      type.baseBehaviourCode === direction &&
      type.allowsFinancialEntries &&
      type.allowsInventoryEntries &&
      type.allowsBillReferences
  );
  if (!validType && goodType) {
    next.voucherTypeId = goodType.id;
    next.voucherClassId = undefined;
  }
  if (!branches.some((entry) => entry.id === next.branchId && entry.isActive) && branch) {
    next.branchId = branch.id;
    if (direction === 'sales') next.placeOfSupplyStateCode = branch.stateCode ?? next.placeOfSupplyStateCode;
  }
  if (!years.some((entry) => entry.id === next.financialYearId && !entry.isClosed) && year) {
    next.financialYearId = year.id;
  }
  if (
    next.voucherClassId &&
    !classes.some(
      (entry) => entry.id === next.voucherClassId && entry.isActive && entry.voucherTypeId === next.voucherTypeId
    )
  ) {
    next.voucherClassId = undefined;
  }
  if (
    next.invoiceSeriesId &&
    !invoiceSeriesMatches(
      series.find((entry) => entry.id === next.invoiceSeriesId) ?? ({} as InvoiceSeries),
      companyId,
      next.branchId,
      next.voucherTypeId,
      direction,
      types,
      branches
    )
  ) {
    next.invoiceSeriesId = undefined;
  }
  return next;
}
function formPayload(
  direction: GoodsDirection,
  draft: Omit<GoodsVoucherPayload, 'lines' | 'additionalCharges'>,
  lines: DraftLine[],
  charges: Array<GoodsAdditionalChargePayload & { key: string }>
): GoodsVoucherPayload {
  const {
    saleType,
    billingAddressId,
    shippingAddressId,
    purchaseType,
    supplierInvoiceNumber,
    supplierInvoiceDate,
    selfInvoiceNumber,
    reverseCharge,
    invoiceSeriesId,
    ...shared
  } = draft;
  const directionFields =
    direction === 'sales'
      ? { saleType, billingAddressId, shippingAddressId, invoiceSeriesId }
      : {
          purchaseType,
          supplierInvoiceNumber,
          supplierInvoiceDate,
          selfInvoiceNumber,
          reverseCharge,
        };
  return {
    ...shared,
    invoiceDiscount: optionalDecimal(shared.invoiceDiscount) ?? '0.0000',
    ...directionFields,
    additionalCharges: charges.filter((x) => x.amount || x.ledgerId).map(({ key, ...x }) => x),
    lines: lines
      .filter((x) => x.itemId || x.itemUnitId || x.actualQuantity || x.billedQuantity || x.unitRate)
      .map(({ key, itemName, unitOptions, discountPercent, discountAmount, discountTradeAmount, ...x }) => ({
        ...x,
        ...(optionalDecimal(discountPercent) ? { discountPercent: optionalDecimal(discountPercent) } : {}),
        ...(optionalDecimal(discountAmount) ? { discountAmount: optionalDecimal(discountAmount) } : {}),
        ...(optionalDecimal(discountTradeAmount)
          ? { discountTradeAmount: optionalDecimal(discountTradeAmount) }
          : {}),
      })),
  };
}

export function GoodsVoucherWorkspacePage({ direction }: { direction: GoodsDirection }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const companyId = useCompanyStore((s) => s.activeCompanyId);
  const companyFeatures = useCompanyStore((s) => s.features);
  const pushToast = useUiStore((s) => s.pushToast);
  const refreshBooks = useUiStore((s) => s.refreshBooks);
  const [types, setTypes] = useState<VoucherType[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [years, setYears] = useState<FinancialYear[]>([]);
  const [parties, setParties] = useState<Ledger[]>([]);
  const [chargeLedgers, setChargeLedgers] = useState<Ledger[]>([]);
  const [classes, setClasses] = useState<VoucherClass[]>([]);
  const [invoiceSeries, setInvoiceSeries] = useState<InvoiceSeries[]>([]);
  const [items, setItems] = useState<
    Array<{
      id: string;
      name: string;
      kind: string;
      standardPurchaseRate: string | null;
      standardSalesRate: string | null;
    }>
  >([]);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selected, setSelected] = useState<GoodsVoucher | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewSupported, setPreviewSupported] = useState(true);
  const [local, setLocal] = useState<LocalGoodsVoucherDraft | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [printBusy, setPrintBusy] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const idempotencyKey = useRef<string | null>(null);
  const previewAbort = useRef<AbortController | null>(null);
  const previousRoute = useRef<string | null>(null);
  const [draft, setDraft] = useState<Omit<GoodsVoucherPayload, 'lines' | 'additionalCharges'>>({
    voucherTypeId: '',
    voucherClassId: undefined,
    branchId: '',
    financialYearId: '',
    voucherDate: today(),
    state: 'regular',
    narration: '',
    reference: null,
    partyLedgerId: '',
    placeOfSupplyStateCode: '',
    saleType: 'b2b',
    purchaseType: 'domestic',
    supplierInvoiceNumber: '',
    supplierInvoiceDate: '',
    selfInvoiceNumber: '',
    reverseCharge: false,
    invoiceSeriesId: undefined,
    invoiceDiscount: '',
  });
  const [lines, setLines] = useState<DraftLine[]>([line()]);
  const [charges, setCharges] = useState<Array<GoodsAdditionalChargePayload & { key: string }>>([]);
  const [dirty, setDirty] = useState(false);
  const draftSaveQueue = useRef(Promise.resolve());
  const draftSaveGeneration = useRef(0);
  const isDetail = Boolean(id && id !== 'new');
  const gstEnabled = companyFeatures?.flags.gst_enabled !== false;
  const type = types.find((x) => x.id === draft.voucherTypeId);
  const title = direction === 'sales' ? 'Sales' : 'Purchase';
  const selectedShippingAddress = addresses.find((address) => address.id === draft.shippingAddressId);
  const eligibleInvoiceSeries = useMemo(
    () =>
      invoiceSeries.filter(
        (series) =>
          invoiceSeriesMatches(
            series,
            companyId ?? '',
            draft.branchId,
            draft.voucherTypeId,
            direction,
            types,
            branches
          )
      ),
    [invoiceSeries, companyId, draft.branchId, draft.voucherTypeId, direction, types, branches]
  );
  const resetCommercialDraft = () => {
    draftSaveGeneration.current += 1;
    idempotencyKey.current = null;
    setSelected(null);
    setPreview(null);
    setPreviewError(null);
    setLines([line()]);
    setCharges([]);
    setDirty(false);
    setDraft((current) => ({
      ...current,
      partyLedgerId: '',
      narration: '',
      reference: null,
      invoiceDiscount: '',
      supplierInvoiceNumber: '',
      supplierInvoiceDate: '',
      selfInvoiceNumber: '',
      reverseCharge: false,
    }));
  };
  const patch = (part: Partial<typeof draft>) => {
    setDraft((x) => ({ ...x, ...part }));
    setDirty(true);
  };
  const patchLine = (key: string, part: Partial<DraftLine>) => {
    setLines((x) => x.map((v) => (v.key === key ? { ...v, ...part } : v)));
    setDirty(true);
  };
  function applyVoucherClass(voucherClassId: string) {
    const selectedClass = classes.find((x) => x.id === voucherClassId);
    const template = selectedClass?.templateData ?? {};
    const text = (key: string) => (typeof template[key] === 'string' ? (template[key] as string) : undefined);
    const bool = (key: string) => (typeof template[key] === 'boolean' ? (template[key] as boolean) : undefined);
    const state = text('state');
    const saleType = text('saleType');
    const purchaseType = text('purchaseType');
    patch({
      voucherClassId: voucherClassId || undefined,
      voucherTypeId: selectedClass?.voucherTypeId ?? draft.voucherTypeId,
      narration: text('narration') ?? draft.narration,
      reference: text('reference') ?? draft.reference,
      invoiceDiscount: text('invoiceDiscount') ?? draft.invoiceDiscount,
      state:
        state === 'regular' || state === 'optional' || state === 'post_dated' || state === 'memorandum'
          ? state
          : draft.state,
      saleType:
        direction === 'sales' && saleType ? (saleType as NonNullable<GoodsVoucherPayload['saleType']>) : draft.saleType,
      purchaseType:
        direction === 'purchase' && purchaseType
          ? (purchaseType as NonNullable<GoodsVoucherPayload['purchaseType']>)
          : draft.purchaseType,
      reverseCharge: direction === 'purchase' ? (bool('reverseCharge') ?? draft.reverseCharge) : draft.reverseCharge,
    });
  }
  useEffect(() => {
    let dead = false;
    if (!companyId) return;
    Promise.all([
      voucherTypesApi.list(),
      branchesApi.list(),
      financialYearsApi.list(),
      ledgersApi.list({ ledgerType: 'party', limit: 200 }),
      ledgersApi.list({ limit: 200 }),
      voucherClassesApi.list(),
      itemsApi.list({ q: '' }),
      invoiceSeriesApi.list({ limit: 200 }),
    ])
      .then(async ([t, b, y, p, l, c, i, series]) => {
        if (dead) return;
        setTypes(t.items);
        setBranches(b.items);
        setYears(y.items);
        setParties(p.items);
        setChargeLedgers(l.items);
        setClasses(c.items);
        setInvoiceSeries(series.items);
        setItems(
          i.items.map((x) => ({
            id: x.id,
            name: x.name,
            kind: x.kind,
            standardPurchaseRate: x.standardPurchaseRate,
            standardSalesRate: x.standardSalesRate,
          }))
        );
        if (!isDetail) {
          const good = preferredGoodsVoucherType(t.items, direction);
          const branch = b.items.find((x) => x.isPrimary && x.isActive) ?? b.items[0];
          const year = y.items.find((x) => !x.isClosed) ?? y.items[0];
          const defaultClass =
            c.items.find(
              (entry) => entry.isActive && entry.code === `standard_${direction}` && entry.voucherTypeId === good?.id
            ) ?? c.items.find((entry) => entry.isActive && entry.voucherTypeId === good?.id);
          const template = defaultClass?.templateData ?? {};
          // Always initialise the visible controlled selects from live masters.
          // A recovered draft is only a candidate until the user explicitly restores it.
          setDraft((current) => ({
            ...current,
            voucherTypeId: good?.id ?? current.voucherTypeId,
            voucherClassId: defaultClass?.id,
            branchId: branch?.id ?? current.branchId,
            placeOfSupplyStateCode: branch?.stateCode ?? current.placeOfSupplyStateCode,
            financialYearId: year?.id ?? current.financialYearId,
            voucherDate: year && today() < year.startsOn ? year.startsOn : current.voucherDate,
            invoiceSeriesId: undefined,
            narration: typeof template.narration === 'string' ? template.narration : current.narration,
            invoiceDiscount:
              typeof template.invoiceDiscount === 'string' ? template.invoiceDiscount : current.invoiceDiscount,
          }));
          try {
            const recovered = await loadGoodsVoucherDraft(companyId, direction);
            if (recovered) {
              idempotencyKey.current = recovered.idempotencyKey;
              const reconciledPayload = reconcileGoodsDraftMasters(
                recovered.payload,
                direction,
                companyId,
                t.items,
                b.items,
                y.items,
                c.items,
                series.items
              );
              const reconciled =
                JSON.stringify(reconciledPayload) === JSON.stringify(recovered.payload)
                  ? recovered
                  : { ...recovered, payload: reconciledPayload, savedAt: new Date().toISOString() };
              setLocal(reconciled);
              if (reconciled !== recovered) void saveGoodsVoucherDraft(reconciled);
            } else {
              // Live-master defaults above are already the clean new document.
            }
          } catch {}
        }
      })
      .catch(setError)
      .finally(() => !dead && setLoading(false));
    return () => {
      dead = true;
    };
  }, [companyId, direction, isDetail]);
  useEffect(() => {
    if (isDetail || !types.length || !branches.length) return;
    const good = preferredGoodsVoucherType(types, direction);
    const branch = branches.find((entry) => entry.isActive && entry.isPrimary) ?? branches.find((entry) => entry.isActive);
    if (!good || !branch) return;
    setDraft((current) => {
      const typeIsValid = types.some(
        (type) =>
          type.id === current.voucherTypeId &&
          type.isActive &&
          type.baseBehaviourCode === direction &&
          type.allowsFinancialEntries &&
          type.allowsInventoryEntries &&
          type.allowsBillReferences
      );
      const branchIsValid = branches.some((entry) => entry.id === current.branchId && entry.isActive);
      if (typeIsValid && branchIsValid) return current;
      return {
        ...current,
        voucherTypeId: typeIsValid ? current.voucherTypeId : good.id,
        voucherClassId: typeIsValid ? current.voucherClassId : undefined,
        branchId: branchIsValid ? current.branchId : branch.id,
        placeOfSupplyStateCode: branchIsValid ? current.placeOfSupplyStateCode : branch.stateCode ?? '',
      };
    });
  }, [types, branches, direction, isDetail]);
  useEffect(() => {
    const route = `${direction}:${id ?? 'new'}`;
    if (previousRoute.current && previousRoute.current !== route && !isDetail) resetCommercialDraft();
    previousRoute.current = route;
  }, [direction, id, isDetail]);
  useEffect(() => {
    if (direction !== 'sales' || isDetail) return;
    setDraft((current) => {
      if (current.invoiceSeriesId && eligibleInvoiceSeries.some((series) => series.id === current.invoiceSeriesId))
        return current;
      const preferred = eligibleInvoiceSeries.length === 1 ? eligibleInvoiceSeries[0] : undefined;
      return { ...current, invoiceSeriesId: preferred?.id };
    });
  }, [direction, isDetail, eligibleInvoiceSeries]);
  useEffect(() => {
    if (!draft.partyLedgerId) return setAddresses([]);
    partyAddressesApi
      .list({ partyLedgerId: draft.partyLedgerId })
      .then((x) => setAddresses(x.items))
      .catch(setError);
  }, [draft.partyLedgerId]);
  useEffect(() => {
    if (!isDetail || !id) return;
    setLoading(true);
    goodsVouchersApi
      .get(id)
      .then(setSelected)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [id, isDetail]);
  const payload = useMemo(() => {
    const value = formPayload(direction, draft, lines, charges);
    if (direction === 'sales')
      return {
        ...value,
        saleType: !gstEnabled ? ('non_gst' as const) : value.saleType,
        placeOfSupplyStateCode: selectedShippingAddress?.stateCode ?? '',
      };
    return value;
  }, [direction, draft, lines, charges, gstEnabled, selectedShippingAddress?.stateCode]);
  useEffect(() => {
    if (
      !previewSupported ||
      isDetail ||
      !payload.voucherTypeId ||
      !payload.branchId ||
      !payload.financialYearId ||
      !payload.partyLedgerId ||
      payload.lines.some((x) => !x.itemId || !x.itemUnitId || !x.actualQuantity || !x.billedQuantity || !x.unitRate) ||
      !payload.lines.length
    )
      return;
    const timer = window.setTimeout(() => {
      previewAbort.current?.abort();
      const controller = new AbortController();
      previewAbort.current = controller;
      goodsVouchersApi
        .preview(direction, payload, controller.signal)
        .then((result: any) => {
          const computation = result.computation ?? result.computed?.computation ?? result;
          setPreview(computation);
          setPreviewError(null);
        })
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === 'AbortError') return;
          if (e instanceof ApiError && e.status === 404) {
            setPreviewSupported(false);
            return;
          }
          setPreview(null);
          setPreviewError(e instanceof Error ? e.message : 'Preview unavailable.');
        });
    }, 450);
    return () => {
      window.clearTimeout(timer);
      previewAbort.current?.abort();
    };
  }, [direction, isDetail, payload, previewSupported]);
  useEffect(() => {
    if (!companyId || isDetail || !dirty) return;
    const draftIdempotencyKey = idempotencyKey.current ?? crypto.randomUUID();
    idempotencyKey.current = draftIdempotencyKey;
    const generation = draftSaveGeneration.current;
    const snapshot: LocalGoodsVoucherDraft = {
      companyId,
      direction,
      payload,
      idempotencyKey: draftIdempotencyKey,
      savedAt: new Date().toISOString(),
    };
    // Queue writes so rapid field edits cannot finish out of order. There is
    // deliberately no debounce: leaving for a master screen must not discard
    // the last item line before IndexedDB receives it.
    draftSaveQueue.current = draftSaveQueue.current
      .then(async () => {
        if (generation !== draftSaveGeneration.current) return;
        await saveGoodsVoucherDraft(snapshot);
        if (generation === draftSaveGeneration.current) setLocal(snapshot);
      })
      .catch(() => undefined);
  }, [companyId, direction, dirty, isDetail, payload]);
  async function chooseItem(key: string, itemId: string) {
    const item = items.find((x) => x.id === itemId);
    patchLine(key, {
      itemId,
      itemName: item?.name ?? '',
      itemUnitId: '',
      unitOptions: [],
    });
    try {
      const units = await itemsApi.units.list(itemId);
      const options = units
        .filter((x) => x.isSalesUnit || x.isPurchaseUnit || x.isStockUnit)
        .map((x) => ({
          id: x.id,
          unitId: x.unitId,
          label: x.unitName ? `${x.unitName}${x.unitSymbol ? ` (${x.unitSymbol})` : ''}` : x.unitCode ?? 'Unnamed UOM',
        }));
      patchLine(key, {
        itemId,
        itemName: item?.name ?? '',
        itemUnitId: options[0]?.id ?? '',
        unitOptions: options,
        unitRate: direction === 'sales' ? (item?.standardSalesRate ?? '') : (item?.standardPurchaseRate ?? ''),
      });
    } catch (cause) {
      setError(cause);
    }
  }
  function validate() {
    if (!payload.voucherTypeId || !payload.branchId || !payload.financialYearId || !payload.partyLedgerId)
      return 'Choose voucher type, branch, financial year and party.';
    if (!/^\d{2}$/.test(payload.placeOfSupplyStateCode ?? ''))
      return 'Place of supply must be a two-digit GST state code.';
    if (!payload.lines.length) return 'Add at least one item line.';
    for (const [i, x] of payload.lines.entries()) {
      if (!x.itemId || !x.itemUnitId) return `Choose an item and UOM on line ${i + 1}.`;
      for (const [name, value, scale] of [
        ['Actual quantity', x.actualQuantity, 6],
        ['Billed quantity', x.billedQuantity, 6],
        ['Free quantity', x.freeQuantity, 6],
        ['Unit rate', x.unitRate, 6],
        ['Discount %', x.discountPercent ?? '', 6],
        ['Discount amount', x.discountAmount ?? '', 4],
        ['Trade discount', x.discountTradeAmount ?? '', 4],
      ] as const)
        if (!money(value, scale)) return `${name} on line ${i + 1} must be an exact non-negative decimal.`;
    }
    for (const c of payload.additionalCharges ?? [])
      if (!c.ledgerId || !money(c.amount, 4) || c.amount === '0')
        return 'Each additional charge needs a ledger and a positive exact amount.';
    if (!money(payload.invoiceDiscount ?? '', 4)) return 'Invoice discount must be an exact non-negative amount.';
    if (direction === 'sales') {
      if (!eligibleInvoiceSeries.length)
        return 'No active Sales invoice series matches this branch and Sales voucher type. Configure one before posting.';
      if (eligibleInvoiceSeries.length > 1 && !payload.invoiceSeriesId)
        return 'Choose an invoice series; multiple active Sales series match this branch and voucher type.';
      if (
        payload.invoiceSeriesId &&
        !eligibleInvoiceSeries.some((series) => series.id === payload.invoiceSeriesId)
      )
        return 'Choose an active invoice series matching the selected branch and Sales voucher type.';
      // With exactly one match, an omitted ID is safe: the server resolves
      // that sole active series. If the UI has selected one, it was checked
      // above against the same current-master set.
    }
    if (direction === 'purchase' && (payload.supplierInvoiceNumber?.trim().length ?? 0) > 16)
      return 'Supplier invoice number must be 16 characters or fewer for the printable purchase identity.';
    if (
      direction === 'sales' &&
      preview?.documentType !== 'bill_of_supply' &&
      (!payload.billingAddressId || !payload.shippingAddressId)
    )
      return 'The server preview identifies this as a tax invoice; choose active bill-to and ship-to addresses.';
    return null;
  }
  async function save() {
    const issue = validate();
    if (issue) {
      setError(new Error(issue));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (!idempotencyKey.current) idempotencyKey.current = crypto.randomUUID();
      const saved = await goodsVouchersApi.create(direction, payload, idempotencyKey.current);
      setSelected(saved);
      setDirty(false);
      draftSaveGeneration.current += 1;
      if (companyId) await clearGoodsVoucherDraft(companyId, direction);
      idempotencyKey.current = null;
      pushToast({ kind: 'success', message: `${saved.displayNumber} posted.` });
      refreshBooks();
      navigate(`/goods/${direction}/${saved.id}`);
    } catch (cause) {
      setError(cause);
    } finally {
      setSaving(false);
    }
  }
  async function cancel() {
    if (!selected || !cancelReason.trim()) return;
    setSaving(true);
    try {
      setSelected(await goodsVouchersApi.cancel(selected.id, selected.revision, cancelReason.trim()));
      setShowCancel(false);
      pushToast({ kind: 'success', message: 'Goods voucher cancelled.' });
      refreshBooks();
    } catch (cause) {
      setError(cause);
    } finally {
      setSaving(false);
    }
  }
  async function print(
    copy?: 'Original for Recipient' | 'Duplicate for Transporter' | 'Triplicate for Supplier',
    share = false
  ) {
    if (!selected) return;
    setPrintBusy(true);
    setDownloadStatus(null);
    try {
      const file = await goodsVouchersApi.print(selected.id, copy);
      if (share) {
        const attachment = new File([file.blob], file.filename, {
          type: file.contentType,
        });
        if (navigator.canShare?.({ files: [attachment] })) {
          await navigator.share({
            files: [attachment],
            title: selected.displayNumber,
          });
          setDownloadStatus(`Shared: ${file.filename}`);
          return;
        }
      }
      setDownloadStatus(`Download started: ${saveDownload(file)}`);
    } catch (cause) {
      setError(cause);
      setDownloadStatus(null);
    } finally {
      setPrintBusy(false);
    }
  }
  async function restore() {
    if (!local) return;
    const restoredPayload = reconcileGoodsDraftMasters(
      local.payload,
      direction,
      companyId ?? '',
      types,
      branches,
      years,
      classes,
      invoiceSeries
    );
    const { lines: storedLines = [], additionalCharges: storedCharges = [], ...header } = restoredPayload;
    try {
      const hydrated = await Promise.all(
        storedLines.map(async (stored) => {
          const item = items.find((entry) => entry.id === stored.itemId);
          const units = stored.itemId ? await itemsApi.units.list(stored.itemId) : [];
          const unitOptions = units
            .filter((unit) => unit.isSalesUnit || unit.isPurchaseUnit || unit.isStockUnit)
            .map((unit) => ({
              id: unit.id,
              unitId: unit.unitId,
              label: unit.unitName ? `${unit.unitName}${unit.unitSymbol ? ` (${unit.unitSymbol})` : ''}` : unit.unitCode ?? 'Unnamed UOM',
            }));
          // Preserve an incomplete or now-inactive line rather than dropping
          // the whole draft. A synthetic option keeps the saved UOM visible so
          // the user can replace it deliberately before posting.
          if (stored.itemUnitId && !unitOptions.some((unit) => unit.id === stored.itemUnitId))
            unitOptions.push({ id: stored.itemUnitId, unitId: 'Saved UOM (inactive)', label: 'Saved UOM (inactive)' });
          return {
            ...stored,
            key: crypto.randomUUID(),
            itemName: item?.name ?? (stored.itemId ? `Saved item · ${stored.itemId.slice(0, 8)}` : undefined),
            unitOptions,
          };
        })
      );
      idempotencyKey.current = local.idempotencyKey || crypto.randomUUID();
      setDraft(header);
      setLines(hydrated);
      setCharges(storedCharges.map((x) => ({ ...x, key: crypto.randomUUID() })));
      setDirty(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error('Draft recovery could not validate every saved item.'));
    }
  }
  useEffect(() => {
    const onGoodsNavigation = (event: Event) => {
      if (!dirty || isDetail) return;
      if (!window.confirm('Discard the current unsaved goods voucher and open a new document?')) {
        event.preventDefault();
        return;
      }
      resetCommercialDraft();
      if (companyId) void clearGoodsVoucherDraft(companyId, direction).then(() => setLocal(null));
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty || isDetail) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('goods-voucher:navigate', onGoodsNavigation);
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('goods-voucher:navigate', onGoodsNavigation);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [companyId, direction, dirty, isDetail]);
  useShortcuts(`${title} voucher`, [
    {
      id: 'save-goods',
      bindings: [{ key: 'a', ctrl: true }],
      description: `Save ${title}`,
      allowInInputs: true,
      when: () => !isDetail && !saving,
      handler: () => void save(),
    },
    {
      id: 'new-line',
      bindings: [{ key: 'i', alt: true }],
      description: 'Add item line',
      allowInInputs: true,
      when: () => !isDetail,
      handler: () => {
        setLines((x) => [...x, line()]);
        setDirty(true);
      },
    },
  ]);
  if (loading)
    return <div className="p-5 text-[12.5px] text-[var(--text-dim)]">Loading {title.toLowerCase()} workspace…</div>;
  if (isDetail && selected)
    return (
      <GoodsDetail
        voucher={selected}
        title={title}
        ledgers={chargeLedgers}
        downloadBusy={printBusy}
        downloadStatus={downloadStatus}
        onBack={() => navigate(`/goods/${direction}/new`)}
        onCancel={() => setShowCancel(true)}
        onPrint={(copy, share) => void print(copy, share)}
        onDismissCancel={() => setShowCancel(false)}
        showCancel={showCancel}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        saving={saving}
        onConfirm={() => void cancel()}
        error={error}
      />
    );
  return (
    <div className="flex min-h-full flex-col bg-[var(--bg)]">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-2">
        <div>
          <p className="text-[10.5px] uppercase tracking-[.12em] text-[var(--accent)]">Goods voucher</p>
          <h1 className="text-[15px] font-semibold">New {title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <span className="kbd">{direction === 'sales' ? 'F8 Sales' : 'F9 Purchase'}</span>
          <button className="button-muted" onClick={() => navigate('/masters/items')}>
            + Create item
          </button>
          <button className="button-primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Posting…' : 'Post voucher'} <span className="kbd">Ctrl+A</span>
          </button>
        </div>
      </header>
      {Boolean(error) && (
        <div className="px-4 pt-2">
          <ErrorBanner error={error} onDismiss={() => setError(null)} />
        </div>
      )}
      {local && !dirty && (
        <div className="mx-4 mt-2 flex items-center gap-2 border border-[var(--border)] bg-[var(--bg-inset)] px-3 py-2 text-[11.5px]">
          <span>
            Unsaved {title.toLowerCase()} draft from {formatTime(local.savedAt)}.
          </span>
          <button className="button-primary" onClick={restore}>
            Restore
          </button>
          <button
            className="button-muted"
            onClick={() => {
              draftSaveGeneration.current += 1;
              idempotencyKey.current = null;
              if (companyId) void clearGoodsVoucherDraft(companyId, direction).then(() => setLocal(null));
            }}
          >
            Clear
          </button>
        </div>
      )}
      <form
        className="mx-auto flex w-full max-w-[1400px] flex-col gap-3 p-4"
        onSubmit={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && (e.target as HTMLElement).tagName !== 'BUTTON') {
            e.preventDefault();
            const all = Array.from(
              e.currentTarget.querySelectorAll<HTMLElement>('input,select,textarea,button')
            ).filter((x) => !x.hasAttribute('disabled'));
            all[all.indexOf(e.target as HTMLElement) + 1]?.focus();
          }
        }}
      >
        <section className="grid grid-cols-2 gap-2 border-b border-[var(--border)] pb-3 md:grid-cols-6">
          <Field label="Voucher type" required>
            <Select value={draft.voucherTypeId} onChange={(e) => patch({ voucherTypeId: e.target.value })}>
              <option value="">Choose {title} type</option>
              {types
                .filter((x) => x.baseBehaviourCode === direction)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name} · {x.code}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Voucher class" hint="Applies saved class defaults">
            <Select value={draft.voucherClassId ?? ''} onChange={(e) => applyVoucherClass(e.target.value)}>
              <option value="">No class</option>
              {classes
                .filter(
                  (x) => x.isActive && types.find((t) => t.id === x.voucherTypeId)?.baseBehaviourCode === direction
                )
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Branch" required>
            <Select
              value={draft.branchId}
              onChange={(e) => {
                const branch = branches.find((x) => x.id === e.target.value);
                patch({
                  branchId: e.target.value,
                  placeOfSupplyStateCode: branch?.stateCode ?? draft.placeOfSupplyStateCode,
                });
              }}
            >
              {branches
                .filter((x) => x.isActive)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Financial year" required>
            <Select value={draft.financialYearId} onChange={(e) => patch({ financialYearId: e.target.value })}>
              {years.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.label}
                  {x.isClosed ? ' · closed' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date" required>
            <TextInput type="date" value={draft.voucherDate} onChange={(e) => patch({ voucherDate: e.target.value })} />
          </Field>
          <Field label="State">
            <Select value={draft.state} onChange={(e) => patch({ state: e.target.value as any })}>
              {['regular', 'optional', 'post_dated', 'memorandum'].map((x) => (
                <option key={x} value={x}>
                  {x.replace('_', ' ')}
                </option>
              ))}
            </Select>
          </Field>
        </section>
        <section className="grid grid-cols-1 gap-2 md:grid-cols-4">
          <Field label={direction === 'sales' ? 'Customer' : 'Supplier'} required>
            <Select
              value={draft.partyLedgerId}
              onChange={(e) =>
                patch({
                  partyLedgerId: e.target.value,
                  billingAddressId: undefined,
                  shippingAddressId: undefined,
                  placeOfSupplyStateCode: direction === 'sales' ? '' : draft.placeOfSupplyStateCode,
                })
              }
            >
              <option value="">Choose party</option>
              {parties.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Place of supply"
            hint={
              direction === 'sales'
                ? 'Derived from the selected Ship-to address; it determines intra/inter-state GST.'
                : 'Defaults to the selected branch state.'
            }
          >
            <TextInput
              value={
                direction === 'sales'
                  ? (selectedShippingAddress?.stateCode ?? '')
                  : (draft.placeOfSupplyStateCode ?? '')
              }
              disabled={direction === 'sales'}
              onChange={(e) => patch({ placeOfSupplyStateCode: e.target.value })}
              placeholder="e.g. 27"
            />
          </Field>
          {direction === 'sales' ? (
            <>
              <Field
                label="Sale type"
                hint={!gstEnabled ? 'GST is disabled for this company; sales post as a Bill of Supply.' : undefined}
              >
                <Select
                  value={gstEnabled ? draft.saleType : 'non_gst'}
                  disabled={!gstEnabled}
                  onChange={(e) => patch({ saleType: e.target.value as any })}
                >
                  {[
                    'b2b',
                    'b2c',
                    'b2c_large',
                    'export_with_lut',
                    'export_with_payment',
                    'sez_with_payment',
                    'sez_without_payment',
                    'deemed_export',
                    'nil_rated',
                    'exempt',
                    'non_gst',
                  ].map((x) => (
                    <option key={x} value={x}>
                      {x.replaceAll('_', ' ')}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Invoice series"
                required={eligibleInvoiceSeries.length > 1}
                hint={
                  eligibleInvoiceSeries.length === 1
                    ? 'Sole active series selected automatically.'
                    : eligibleInvoiceSeries.length > 1
                      ? 'Select the series that should issue this invoice.'
                      : 'No active series matches this branch and Sales voucher type.'
                }
              >
                <Select
                  value={draft.invoiceSeriesId ?? ''}
                  onChange={(e) => patch({ invoiceSeriesId: e.target.value || undefined })}
                  disabled={!eligibleInvoiceSeries.length}
                >
                  <option value="">
                    {eligibleInvoiceSeries.length ? 'Choose invoice series' : 'No active invoice series'}
                  </option>
                  {eligibleInvoiceSeries.map((series) => (
                    <option key={series.id} value={series.id}>
                      {series.name} · {series.prefix}…{series.suffix}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Bill-to / Ship-to"
                hint={
                  preview?.documentType === 'bill_of_supply'
                    ? 'Bill of Supply: particulars remain useful for printing.'
                    : 'Required when the server classifies this as a tax invoice.'
                }
              >
                <div className="grid grid-cols-2 gap-1">
                  <Select
                    value={draft.billingAddressId ?? ''}
                    onChange={(e) => patch({ billingAddressId: e.target.value || undefined })}
                  >
                    <option value="">Bill-to</option>
                    {addresses
                      .filter((x) => x.addressKind === 'billing' || x.addressKind === 'other')
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.label || x.addressLine1}
                        </option>
                      ))}
                  </Select>
                  <Select
                    value={draft.shippingAddressId ?? ''}
                    onChange={(e) => {
                      const shippingAddress = addresses.find((address) => address.id === e.target.value);
                      patch({
                        shippingAddressId: e.target.value || undefined,
                        placeOfSupplyStateCode: shippingAddress?.stateCode ?? '',
                      });
                    }}
                  >
                    <option value="">Ship-to</option>
                    {addresses
                      .filter((x) => x.addressKind === 'shipping' || x.addressKind === 'other')
                      .map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.label || x.addressLine1}
                        </option>
                      ))}
                  </Select>
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <button
                    type="button"
                    className="text-[11px] text-[var(--accent)] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!draft.billingAddressId}
                    onClick={() => {
                      const billingAddress = addresses.find((address) => address.id === draft.billingAddressId);
                      patch({
                        shippingAddressId: draft.billingAddressId,
                        placeOfSupplyStateCode: billingAddress?.stateCode ?? '',
                      });
                    }}
                  >
                    Same as bill-to
                  </button>
                  {draft.billingAddressId && draft.billingAddressId === draft.shippingAddressId && (
                    <span className="text-[10.5px] text-[var(--text-faint)]">Ship-to uses the bill-to address.</span>
                  )}
                </div>
                {draft.partyLedgerId && !addresses.length && (
                  <button
                    type="button"
                    className="mt-1 text-[11px] text-[var(--accent)] underline-offset-2 hover:underline"
                    onClick={() => navigate('/masters/party-addresses')}
                  >
                    Add party bill-to / ship-to particulars →
                  </button>
                )}
              </Field>
            </>
          ) : (
            <>
              <Field label="Purchase type">
                <Select value={draft.purchaseType} onChange={(e) => patch({ purchaseType: e.target.value as any })}>
                  {['domestic', 'import', 'reverse_charge', 'deemed_import'].map((x) => (
                    <option key={x} value={x}>
                      {x.replace('_', ' ')}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Supplier invoice">
                <div className="grid grid-cols-2 gap-1">
                  <TextInput
                    value={draft.supplierInvoiceNumber ?? ''}
                    onChange={(e) => patch({ supplierInvoiceNumber: e.target.value })}
                    placeholder="Invoice no."
                    maxLength={16}
                  />
                  <TextInput
                    type="date"
                    value={draft.supplierInvoiceDate ?? ''}
                    onChange={(e) => patch({ supplierInvoiceDate: e.target.value })}
                  />
                </div>
              </Field>
              <div className="md:col-span-2">
                <Toggle
                  checked={Boolean(draft.reverseCharge)}
                  onChange={(v) =>
                    patch({
                      reverseCharge: v,
                      purchaseType: v ? 'reverse_charge' : draft.purchaseType,
                    })
                  }
                  label="Reverse charge mechanism (RCM)"
                />
                <p className="mt-1 text-[11px] text-[var(--text-faint)]">
                  RCM is a posting instruction; eligibility is checked by the server.
                </p>
              </div>
            </>
          )}
        </section>
        <LinesGrid
          direction={direction}
          lines={lines}
          items={items}
          onChoose={chooseItem}
          patch={patchLine}
          onCreateItem={() => navigate('/masters/items')}
          remove={(key) => {
            setLines((x) => (x.length > 1 ? x.filter((v) => v.key !== key) : x));
            setDirty(true);
          }}
          add={() => {
            setLines((x) => [...x, line()]);
            setDirty(true);
          }}
        />
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_360px]">
          <Charges
            charges={charges}
            ledgers={chargeLedgers}
            setCharges={(x) => {
              setCharges(x);
              setDirty(true);
            }}
            direction={direction}
          />
          <section className="space-y-2 border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
            <Field label="Invoice-level discount">
              <TextInput
                inputMode="decimal"
                value={draft.invoiceDiscount ?? ''}
                onChange={(e) => patch({ invoiceDiscount: e.target.value })}
                placeholder="0.0000"
              />
            </Field>
            <Field label="Narration">
              <TextInput
                value={draft.narration ?? ''}
                onChange={(e) => patch({ narration: e.target.value })}
                placeholder="Commercial note"
              />
            </Field>
            <Field label="Internal reference">
              <TextInput value={draft.reference ?? ''} onChange={(e) => patch({ reference: e.target.value || null })} />
            </Field>
            <PreviewCard preview={preview} error={previewError} supported={previewSupported} />
            <p className="text-[10.5px] text-[var(--text-faint)]">
              Bill-wise New Ref is automatic and tied to the server-computed payable. Final GST, discount allocation,
              charges, and debit/credit reconciliation are server-authoritative.
            </p>
          </section>
        </section>
        <div className="flex justify-end gap-2 border-t border-[var(--border)] pt-3">
          <button className="button-muted" onClick={() => navigate('/vouchers')}>
            Esc cancel
          </button>
          <button className="button-primary" disabled={saving} onClick={() => void save()}>
            {saving ? 'Posting…' : `Post ${title}`} <span className="kbd">Ctrl+A</span>
          </button>
        </div>
      </form>
    </div>
  );
}

function LinesGrid({
  direction,
  lines,
  items,
  onChoose,
  patch,
  onCreateItem,
  remove,
  add,
}: {
  direction: GoodsDirection;
  lines: DraftLine[];
  items: Array<{ id: string; name: string; kind: string }>;
  onChoose: (key: string, id: string) => void;
  patch: (key: string, p: Partial<DraftLine>) => void;
  onCreateItem: () => void;
  remove: (key: string) => void;
  add: () => void;
}) {
  return (
    <section className="overflow-auto border border-[var(--border)] bg-[var(--bg-elevated)]">
      <div className="grid min-w-[1180px] grid-cols-[2rem_15rem_9rem_6rem_6rem_6rem_7rem_6rem_6rem_2rem] gap-1 border-b border-[var(--border-strong)] bg-[var(--bg-inset)] px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-[var(--text-dim)]">
        <span>#</span>
        <span>Item / UOM</span>
        <span>Actual / billed / free</span>
        <span>Rate</span>
        <span>Disc %</span>
        <span>Disc ₹</span>
        <span>Trade disc ₹</span>
        <span>{direction === 'purchase' ? 'ITC' : 'Line note'}</span>
        <span>Narration</span>
        <span />
      </div>
      {lines.map((x, i) => (
        <div
          key={x.key}
          className="grid min-w-[1180px] grid-cols-[2rem_15rem_9rem_6rem_6rem_6rem_7rem_6rem_6rem_2rem] items-center gap-1 border-b border-[var(--border)] px-2 py-1"
        >
          <span className="num text-[11px] text-[var(--text-faint)]">{i + 1}</span>
          <div>
            <TypeAhead
              items={items.map((item) => ({
                key: item.id,
                label: item.name,
                sublabel: item.kind,
              }))}
              onSelect={(item) => void onChoose(x.key, item.key)}
              placeholder={x.itemName ? `Selected: ${x.itemName}` : 'Find item…'}
              emptyMessage="No matching item — create one"
            />
            <div className="mt-1 flex items-center gap-1">
              <Select value={x.itemUnitId} onChange={(e) => patch(x.key, { itemUnitId: e.target.value })}>
                <option value="">Transaction UOM</option>
                {x.unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.label}
                  </option>
                ))}
              </Select>
              <button type="button" title="Create item" className="button-muted shrink-0 px-1.5" onClick={onCreateItem}>
                +
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1">
            <TextInput
              title="Stock movement quantity"
              inputMode="decimal"
              value={x.actualQuantity}
              onChange={(e) => patch(x.key, { actualQuantity: e.target.value })}
              placeholder="Actual"
            />
            <TextInput
              title="GST/accounting quantity"
              inputMode="decimal"
              value={x.billedQuantity}
              onChange={(e) => patch(x.key, { billedQuantity: e.target.value })}
              placeholder="Billed"
            />
            <TextInput
              title="Explicit free quantity"
              inputMode="decimal"
              value={x.freeQuantity}
              onChange={(e) => patch(x.key, { freeQuantity: e.target.value })}
              placeholder="Free"
            />
          </div>
          <TextInput
            inputMode="decimal"
            value={x.unitRate}
            onChange={(e) => patch(x.key, { unitRate: e.target.value })}
            placeholder="0.000000"
          />
          <TextInput
            inputMode="decimal"
            value={x.discountPercent ?? ''}
            onChange={(e) => patch(x.key, { discountPercent: e.target.value })}
          />
          <TextInput
            inputMode="decimal"
            value={x.discountAmount ?? ''}
            onChange={(e) => patch(x.key, { discountAmount: e.target.value })}
          />
          <TextInput
            inputMode="decimal"
            value={x.discountTradeAmount ?? ''}
            onChange={(e) => patch(x.key, { discountTradeAmount: e.target.value })}
          />
          {direction === 'purchase' ? (
            <Select
              value={x.itcEligibility ?? 'eligible'}
              onChange={(e) => patch(x.key, { itcEligibility: e.target.value as any })}
            >
              {['eligible', 'ineligible_section_17_5', 'capital_goods', 'input_service'].map((v) => (
                <option key={v} value={v}>
                  {v.replaceAll('_', ' ')}
                </option>
              ))}
            </Select>
          ) : (
            <span className="text-[11px] text-[var(--text-faint)]">Server-resolved GST</span>
          )}
          <TextInput value={x.narration ?? ''} onChange={(e) => patch(x.key, { narration: e.target.value })} />
          <button type="button" className="text-[var(--danger)]" onClick={() => remove(x.key)}>
            ×
          </button>
        </div>
      ))}
      <div className="flex items-center justify-between px-2 py-2">
        <span className="text-[11px] text-[var(--text-faint)]">
          Actual moves inventory; billed drives GST/accounting. Free quantity is explicit.
        </span>
        <button type="button" className="button-muted" onClick={add}>
          + Item <span className="kbd">Alt+I</span>
        </button>
      </div>
    </section>
  );
}
function Charges({
  charges,
  ledgers,
  setCharges,
  direction,
}: {
  charges: Array<GoodsAdditionalChargePayload & { key: string }>;
  ledgers: Ledger[];
  setCharges: (x: Array<GoodsAdditionalChargePayload & { key: string }>) => void;
  direction: GoodsDirection;
}) {
  const change = (key: string, p: Partial<GoodsAdditionalChargePayload>) =>
    setCharges(charges.map((x) => (x.key === key ? { ...x, ...p } : x)));
  return (
    <section className="border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
      <div className="mb-2 flex justify-between">
        <h2 className="text-[12px] font-semibold">Additional charges</h2>
        <button className="button-muted" type="button" onClick={() => setCharges([...charges, charge()])}>
          + Charge
        </button>
      </div>
      {!charges.length && (
        <p className="text-[11px] text-[var(--text-faint)]">
          Freight, packing, insurance and other explicit consideration are apportioned by the server.
        </p>
      )}
      {Boolean(charges.length) && (
        <p className="mb-2 text-[10.5px] text-[var(--text-faint)]">
          Choose a dedicated income/expense ledger for this charge. Never use the Sales or Purchase posting ledger;
          the app does not guess or silently choose an account.
        </p>
      )}
      {charges.map((c) => (
        <div key={c.key} className="mb-2 grid grid-cols-[1fr_1fr_6rem_24px] gap-1">
          <Select value={c.kind} onChange={(e) => change(c.key, { kind: e.target.value as any })}>
            {['freight', 'packing', 'insurance', 'loading', 'handling', 'other'].map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </Select>
          <Select value={c.ledgerId} onChange={(e) => change(c.key, { ledgerId: e.target.value })}>
            <option value="">Charge ledger</option>
            {ledgers.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </Select>
          <TextInput
            inputMode="decimal"
            value={c.amount}
            onChange={(e) => change(c.key, { amount: e.target.value })}
            placeholder="0.0000"
          />
          <button
            type="button"
            className="text-[var(--danger)]"
            onClick={() => setCharges(charges.filter((x) => x.key !== c.key))}
          >
            ×
          </button>
          {direction === 'purchase' && (
            <div className="col-span-4">
              <Toggle
                checked={Boolean(c.capitaliseIntoStock)}
                onChange={(v) => change(c.key, { capitaliseIntoStock: v })}
                label="Capitalise into stock"
              />
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
function PreviewCard({
  preview,
  error,
  supported,
}: {
  preview: Preview | null;
  error: string | null;
  supported: boolean;
}) {
  return (
    <div className="border-t border-[var(--border)] pt-2">
      <p className="text-[10.5px] font-medium uppercase tracking-wide text-[var(--text-dim)]">
        Server calculation preview
      </p>
      {!supported ? (
        <p className="mt-1 text-[11px] text-[var(--text-faint)]">
          Preview service is not available yet; no local GST estimate is shown.
        </p>
      ) : error ? (
        <p className="mt-1 text-[11px] text-[var(--warn)]">{error}</p>
      ) : preview?.totals ? (
        <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
          {preview.documentType && (
            <span className="col-span-2 text-[var(--accent)]">
              Server document: {preview.documentType.replaceAll('_', ' ')}
            </span>
          )}
          <span>
            Taxable <b className="num">{preview.totals.principalValueAfterDiscounts ?? '—'}</b>
          </span>
          <span>
            Payable <b className="num">{preview.totals.counterpartyPayable ?? '—'}</b>
          </span>
          <span>
            CGST <b className="num">{preview.totals.taxes?.cgst ?? '0.0000'}</b>
          </span>
          <span>
            SGST / UTGST <b className="num">{preview.totals.taxes?.sgst ?? preview.totals.taxes?.utgst ?? '0.0000'}</b>
          </span>
          <span>
            IGST <b className="num">{preview.totals.taxes?.igst ?? '0.0000'}</b>
          </span>
          <span>
            Round-off <b className="num">{preview.totals.roundOff ?? '0.0000'}</b>
          </span>
        </div>
      ) : (
        <p className="mt-1 text-[11px] text-[var(--text-faint)]">
          Complete header and item lines to request a server calculation.
        </p>
      )}
    </div>
  );
}
function GoodsDetail({
  voucher,
  title,
  ledgers,
  downloadBusy,
  downloadStatus,
  onBack,
  onCancel,
  onPrint,
  onDismissCancel,
  showCancel,
  cancelReason,
  setCancelReason,
  saving,
  onConfirm,
  error,
}: {
  voucher: GoodsVoucher;
  title: string;
  ledgers: Ledger[];
  downloadBusy: boolean;
  downloadStatus: string | null;
  onBack: () => void;
  onCancel: () => void;
  onPrint: (
    copy?: 'Original for Recipient' | 'Duplicate for Transporter' | 'Triplicate for Supplier',
    share?: boolean
  ) => void;
  onDismissCancel: () => void;
  showCancel: boolean;
  cancelReason: string;
  setCancelReason: (x: string) => void;
  saving: boolean;
  onConfirm: () => void;
  error: unknown;
}) {
  const [copy, setCopy] = useState<'Original for Recipient' | 'Duplicate for Transporter' | 'Triplicate for Supplier'>(
    'Original for Recipient'
  );
  const totals = voucher.computed?.computation?.totals;
  const ledgerNames = new Map(ledgers.map((ledger) => [ledger.id, ledger.name]));
  const issuedSeries = voucher.computed?.invoiceSeries ?? voucher.computed?.invoiceSnapshot?.invoiceSeries;
  const sample = new File([new Blob([''])], 'invoice.pdf', { type: 'application/pdf' });
  const canShare = typeof navigator.canShare === 'function' && navigator.canShare({ files: [sample] });
  return (
    <div className="min-h-full bg-[var(--bg)]">
      <header className="flex items-center justify-between border-b border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-2">
        <div>
          <button className="text-[var(--accent)]" onClick={onBack}>
            ← New {title}
          </button>
          <h1 className="text-[15px] font-semibold">
            {voucher.displayNumber} · {title}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {!voucher.cancelledAt && (
            <button className="button-danger" onClick={onCancel}>
              Cancel voucher
            </button>
          )}
          <Select value={copy} className="w-40" onChange={(event) => setCopy(event.target.value as typeof copy)}>
            <option value="Original for Recipient">Original</option>
            <option value="Duplicate for Transporter">Duplicate</option>
            <option value="Triplicate for Supplier">Triplicate</option>
          </Select>
          <button
            className="button-muted"
            disabled={downloadBusy}
            onClick={() => onPrint(copy)}
            title="Download the server-rendered immutable invoice PDF"
          >
            {downloadBusy ? 'Preparing PDF…' : 'Download PDF'}
          </button>
          {canShare && (
            <button className="button-muted" onClick={() => onPrint(copy, true)}>
              Share
            </button>
          )}
        </div>
      </header>
      {downloadStatus && (
        <p className="px-4 pt-2 text-[11px] text-[var(--success)]" role="status" aria-live="polite">
          {downloadStatus}
        </p>
      )}
      {Boolean(error) && (
        <div className="p-4">
          <ErrorBanner error={error} />
        </div>
      )}
      <div className="grid max-w-5xl grid-cols-1 gap-3 p-4 md:grid-cols-2">
        <section className="border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
          <p className="text-[11px] text-[var(--text-faint)]">
            {voucher.voucherDate} · revision {voucher.revision}
          </p>
          <p className={voucher.cancelledAt ? 'badge-danger' : 'text-[var(--success)]'}>
            {voucher.cancelledAt ? 'Cancelled' : 'Posted'}
          </p>
          {issuedSeries && (
            <p className="mt-1 text-[11px] text-[var(--text-dim)]">
              Issued series: {issuedSeries.name ?? issuedSeries.code ?? issuedSeries.id}
            </p>
          )}
          <p className="mt-2 text-[12px]">{voucher.narration || 'No narration'}</p>
        </section>
        <section className="border border-[var(--border)] bg-[var(--bg-elevated)] p-3">
          <h2 className="text-[12px] font-semibold">Server final totals</h2>
          <p className="mt-2 text-[12px]">
            Counterparty payable <b className="num">{totals?.counterpartyPayable ?? '—'}</b>
          </p>
          <p className="text-[12px]">
            GST assessable value <b className="num">{totals?.assessableValue ?? '—'}</b>
          </p>
          <p className="text-[12px]">
            GST taxable value <b className="num">{totals?.gstTaxableValue ?? '—'}</b>
          </p>
          <p className="text-[12px]">
            Net goods / principal <b className="num">{totals?.principalValueAfterDiscounts ?? '—'}</b>
          </p>
          <p className="text-[12px]">
            Additional charges <b className="num">{totals?.additionalCharges ?? '—'}</b>
          </p>
          <p className="text-[12px]">
            Total tax <b className="num">{totals?.totalTax ?? '—'}</b>
          </p>
          <p className="text-[12px]">
            Round-off <b className="num">{totals?.roundOff ?? '—'}</b>
          </p>
          {Object.entries(totals?.taxes ?? {}).map(([component, amount]) => (
            <p className="text-[12px]" key={component}>
              {component.toUpperCase()} tax <b className="num">{amount}</b>
            </p>
          ))}
        </section>
        <section className="md:col-span-2 border border-[var(--border)] bg-[var(--bg-elevated)]">
          <table className="w-full text-[12px]">
            <thead>
              <tr>
                <th className="table-head">Ledger</th>
                <th className="table-head text-right">Debit</th>
                <th className="table-head text-right">Credit</th>
                <th className="table-head">Narration</th>
              </tr>
            </thead>
            <tbody>
              {voucher.financialEntries.map((x) => (
                <tr key={x.id}>
                  <td className="table-cell">
                    {ledgerNames.get(x.ledgerId) ?? `Unknown ledger · ${x.ledgerId}`}
                  </td>
                  <td className="table-cell num text-right">{x.debit}</td>
                  <td className="table-cell num text-right">{x.credit}</td>
                  <td className="table-cell">{x.narration || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
      {showCancel && (
        <Modal
          open
          onClose={onDismissCancel}
          title={`Cancel ${voucher.displayNumber}`}
          footer={
            <>
              <button className="button-muted" onClick={onDismissCancel}>
                Keep voucher
              </button>
              <button className="button-danger" disabled={!cancelReason.trim() || saving} onClick={onConfirm}>
                Confirm cancellation
              </button>
            </>
          }
        >
          <p className="mb-2 text-[12px] text-[var(--text-dim)]">
            Cancellation reverses the financial and inventory effect while retaining document history.
          </p>
          <TextInput
            autoFocus
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Reason required"
          />
        </Modal>
      )}
    </div>
  );
}
